import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { LocationOnDepartmentApiService } from '../../services/location-on-department/location-on-department-api.service';
import {
  CreateLocationOnDepartment,
  DeleteLocationOnDepartment,
  LoadAllLocationOnDepartments,
  LoadLocationOnDepartmentById,
  LoadLocationOnDepartments,
  UpdateLocationOnDepartment,
  ClearCurrentLocationOnDepartment,
} from './non-conformance-location-on-department.actions';

export interface LocationOnDepartmentStateModel {
  locationOnDepartments: IdName[];
  allLocationOnDepartments: IdName[];
  locationOnDepartmentTotalPages: number;
  currentLocationOnDepartment: IdName | undefined;
}

const NC_LOCATION_ON_DEPARTMENT_STATE_TOKEN = new StateToken<LocationOnDepartmentStateModel>(
  'ncLocationOnDepartments'
);

@State<LocationOnDepartmentStateModel>({
  name: NC_LOCATION_ON_DEPARTMENT_STATE_TOKEN,
  defaults: {
    locationOnDepartments: [],
    allLocationOnDepartments: [],
    locationOnDepartmentTotalPages: 0,
    currentLocationOnDepartment: undefined,
  },
})
@Injectable()
export class NonConformanceLocationOnDepartmentState {
  constructor(private service: LocationOnDepartmentApiService) {}

  @Action(LoadLocationOnDepartments)
  loadLocationOnDepartments(
    { getState, setState }: StateContext<LocationOnDepartmentStateModel>,
    { filter }: LoadLocationOnDepartments
  ) {
    return this.service.loadLocationOnDepartment(filter).pipe(
      tap((response: ApiResponse<IdName[]>) => {
        const state = getState();
        setState({
          ...state,
          locationOnDepartments: response.data,
          locationOnDepartmentTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllLocationOnDepartments, { cancelUncompleted: true })
  loadAllLocationOnDepartments(ctx: StateContext<LocationOnDepartmentStateModel>) {
    return this.service.loadAllLocationOnDepartment().pipe(
      tap((allLocationOnDepartments: IdName[]) => {
        ctx.setState(patch({ allLocationOnDepartments }));
      })
    );
  }

  @Action(LoadLocationOnDepartmentById, { cancelUncompleted: true })
  loadLocationOnDepartmentById(
    { getState, setState }: StateContext<LocationOnDepartmentStateModel>,
    { id }: LoadLocationOnDepartmentById
  ) {
    const { locationOnDepartments } = getState();
    const exist = locationOnDepartments.some((i) => i.id === id);
    return this.service.loadLocationOnDepartmentById(id).pipe(
      tap((LocationOnDepartment: IdName) => {
        setState(
          patch({
            locationOnDepartments: exist
              ? updateItem<IdName>((toUpdate) => toUpdate?.id === id, LocationOnDepartment)
              : insertItem<IdName>(LocationOnDepartment),
            currentLocationOnDepartment: LocationOnDepartment,
          })
        );
      })
    );
  }

  @Action(CreateLocationOnDepartment)
  @OnSuccess({
    url: AdminNonConformanceRoutes.LocationOnDepartment,
    message: 'locationOnDepartmentAdded',
  })
  createLocationOnDepartment(
    { setState }: StateContext<LocationOnDepartmentStateModel>,
    { locationOnDepartment }: CreateLocationOnDepartment
  ) {
    return this.service.createLocationOnDepartment(locationOnDepartment).pipe(
      tap((response: IdName) => {
        setState(
          patch({
            locationOnDepartments: insertItem<IdName>(response),
          })
        );
      })
    );
  }

  @Action(UpdateLocationOnDepartment)
  @OnSuccess({
    url: AdminNonConformanceRoutes.LocationOnDepartment,
    message: 'locationOnDepartmentUpdated',
  })
  updateLocationOnDepartment(
    { setState }: StateContext<LocationOnDepartmentStateModel>,
    { locationOnDepartment }: UpdateLocationOnDepartment
  ) {
    return this.service.updateLocationOnDepartment(locationOnDepartment).pipe(
      tap(() => {
        setState(
          patch({
            locationOnDepartments: updateItem<IdName>(
              (toUpdate) => toUpdate?.id === locationOnDepartment.id,
              locationOnDepartment
            ),
          })
        );
      })
    );
  }

  @Action(DeleteLocationOnDepartment, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminNonConformanceRoutes.LocationOnDepartment,
    message: 'locationOnDepartmentDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteLocationOnDepartment(
    ctx: StateContext<LocationOnDepartmentStateModel>,
    { id }: DeleteLocationOnDepartment
  ) {
    return this.service.deleteLocationOnDepartment(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            locationOnDepartments: removeItem<IdName>(
              (LocationOnDepartmentToDelete: IdName | undefined) =>
                LocationOnDepartmentToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentLocationOnDepartment)
  clearCurrentLocationOnDepartment(ctx: StateContext<LocationOnDepartmentStateModel>) {
    ctx.setState(patch({ currentLocationOnDepartment: undefined }));
  }

  @Selector()
  static getLocationOnDepartmentsTotalPages(state: LocationOnDepartmentStateModel) {
    return state.locationOnDepartmentTotalPages;
  }

  @Selector()
  static locationOnDepartments(state: LocationOnDepartmentStateModel) {
    return state.locationOnDepartments;
  }

  @Selector()
  static allLocationOnDepartments(state: LocationOnDepartmentStateModel): IdName[] {
    return state.allLocationOnDepartments;
  }

  @Selector()
  static getLocationOnDepartmentById(state: LocationOnDepartmentStateModel) {
    return (id: string) => {
      return state.locationOnDepartments.find(
        (LocationOnDepartment: IdName) => LocationOnDepartment.id === id
      );
    };
  }

  @Selector()
  static getCurrentLocationOnDepartment(state: LocationOnDepartmentStateModel) {
    return state.currentLocationOnDepartment;
  }
}
